import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Loader2, ShoppingBag, FileText, Plus, ArrowRight, Check } from 'lucide-react';
import { trpc } from '@/lib/api/trpc';
import { useToast } from '@/components/ui/use-toast';
import { STEP_ICONS, STEP_TYPE_LABELS } from '../../../[id]/storefront/constants/stepStyles';
import type { StepType } from '../../../[id]/storefront/types';
import { Plugin } from './types';

interface PluginInstallationWizardProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  plugin: Plugin;
  selectedDeploymentId?: string;
  selectedPreset?: string;
  storeId: string;
  /** If provided, skip wizard and install directly to this step */
  directInstallContext?: {
    funnelId: string;
    stepId: string;
    stepType: StepType;
  };
  /** Optional funnel ID to pre-select when installing from step editor */
  contextFunnelId?: string;
  /** Optional step type to pre-select when installing from step editor */
  contextStepType?: string;
  onSuccess: (result: { instanceId: string; funnelId?: string; shouldOpenEditor: boolean }) => void;
}

type WizardStep = 'choose-context' | 'choose-funnel' | 'choose-step-type' | 'create-funnel';

// Valid step types for funnel steps (must match backend schema)
// Note: 'external' is NOT a valid funnel step type
const AVAILABLE_STEP_TYPES: StepType[] = ['landing', 'checkout', 'offer', 'thankyou', 'custom', 'external'];

/**
 * Smart Plugin Installation Wizard
 * Guides users through installing a plugin based on their context
 */
export function PluginInstallationWizard({
  open,
  onOpenChange,
  plugin,
  selectedDeploymentId,
  selectedPreset = 'default',
  storeId,
  directInstallContext,
  contextFunnelId,
  contextStepType,
  onSuccess,
}: PluginInstallationWizardProps) {
  const { t } = useTranslation(['storefront', 'common']);
  const { toast } = useToast();
  const utils = trpc.useUtils();

  // Wizard state
  // If context is provided (from step editor), skip the wizard and use context values
  const hasContext = contextFunnelId && contextStepType;
  const [currentStep, setCurrentStep] = useState<WizardStep>(hasContext ? 'choose-funnel' : 'choose-context');
  const [installContext, setInstallContext] = useState<'existing' | 'new' | null>(
    hasContext ? 'existing' : null,
  );
  const [selectedFunnelId, setSelectedFunnelId] = useState<string | null>(contextFunnelId || null);
  const [selectedStepType, setSelectedStepType] = useState<StepType | null>(
    (contextStepType as StepType) || null,
  );
  const [newFunnelName, setNewFunnelName] = useState('');

  // Get deployment (or use latest)
  const deployment = selectedDeploymentId
    ? plugin.deployments.find((d) => d.deploymentId === selectedDeploymentId)
    : plugin.deployments[0];

  // Check which step types have compatible pages
  const getCompatibleStepTypes = (): Set<StepType> => {
    const compatible = new Set<StepType>();

    // If no pages available, still allow 'custom' type
    if (!deployment?.pages || deployment.pages.length === 0) {
      compatible.add('custom');
      return compatible;
    }

    const pages = deployment.pages;

    for (const page of pages) {
      const path = page.path?.toLowerCase() || '';

      // Match common page paths to step types
      if (path.includes('checkout') || path.includes('/op')) {
        compatible.add('checkout');
      }
      if (path.includes('thank') || path.includes('confirmation')) {
        compatible.add('thankyou');
      }
      if (path.includes('offer') || path.includes('upsell') || path.includes('bump')) {
        compatible.add('offer');
      }
      if (path.includes('landing') || path.includes('home') || path === '/') {
        compatible.add('landing');
      }
    }

    // Custom can always be used with any plugin
    compatible.add('custom');

    return compatible;
  };

  const compatibleStepTypes = getCompatibleStepTypes();

  // Fetch funnels for selection
  const { data: funnels, isLoading: isLoadingFunnels } = trpc.funnels.list.useQuery(
    { storeId },
    { enabled: open && installContext === 'existing' },
  );

  // Mutations
  const instantiateMutation = trpc.plugins.instantiate.useMutation({
    onSuccess: async (result) => {
      console.log('[Wizard] ✅ Instantiate mutation success:', {
        instanceId: result.instanceId,
        selectedFunnelId,
        resultKeys: Object.keys(result),
        fullResult: result,
      });

      utils.plugins.listInstances.invalidate();
      utils.plugins.list.invalidate();

      toast({
        title: t('common:success'),
        description: t('plugins.wizard.toast.openingEditor'),
      });

      // Return result to parent (will open config editor)
      // The openConfigEditor call in handleWizardSuccess will automatically close the wizard
      console.log('[Wizard] 🎬 Calling onSuccess to open config editor');
      onSuccess({
        instanceId: result.instanceId,
        funnelId: selectedFunnelId || undefined,
        shouldOpenEditor: true,
      });

      // Don't close wizard manually - openConfigEditor() handles it
    },
    onError: (error) => {
      toast({
        title: t('common:error'),
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const createFunnelMutation = trpc.funnels.create.useMutation({
    onSuccess: async (funnel) => {
      utils.funnels.list.invalidate();
      toast({
        title: t('plugins.wizard.toast.funnelCreatedTitle'),
        description: t('plugins.wizard.toast.funnelCreatedDesc', { name: funnel.name }),
      });

      // Now instantiate the plugin for this funnel
      handleInstantiate(funnel.id);
    },
    onError: (error) => {
      toast({
        title: t('common:error'),
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  // Handle direct install (from step editor)
  React.useEffect(() => {
    if (open && directInstallContext && deployment) {
      // Skip wizard, create instance immediately with isPreview=true
      instantiateMutation.mutate({
        deploymentId: deployment.deploymentId,
        storeId,
        config: {}, // Empty config - backend will load preset
        presetName: selectedPreset, // Backend will load this preset from deployment.configPresets
        isPreview: true, // Create as preview for editing
        reuseIfExists: false, // Always create new instance from editor
      });
    }
  }, [open, directInstallContext, deployment?.deploymentId, selectedPreset]);

  // Handle context install (from step editor via marketplace)
  React.useEffect(() => {
    if (
      open &&
      hasContext &&
      contextFunnelId &&
      contextStepType &&
      deployment &&
      !instantiateMutation.isPending
    ) {
      // Auto-trigger installation when wizard opens with context
      handleInstantiate(contextFunnelId);
    }
  }, [open, hasContext, contextFunnelId, contextStepType, deployment?.deploymentId]);

  // Handle instance creation
  const handleInstantiate = async (targetFunnelId?: string) => {
    if (!deployment) {
      toast({
        title: t('common:error'),
        description: t('plugins.wizard.toast.noDeployment'),
        variant: 'destructive',
      });
      return;
    }

    if (!selectedStepType) {
      toast({
        title: t('common:error'),
        description: t('plugins.wizard.toast.noStepType'),
        variant: 'destructive',
      });
      return;
    }

    // Create instance with selected preset - send presetName to backend
    // Backend will load preset config from deployment.configPresets[presetName]
    // IMPORTANT: This does NOT mount the instance to any funnel step
    // User must manually publish and mount after configuring
    instantiateMutation.mutate({
      deploymentId: deployment.deploymentId,
      storeId,
      config: {
        stepType: selectedStepType, // Override stepType for funnel context
      },
      presetName: selectedPreset, // Backend will merge preset config with above config
      isPreview: true, // Create as preview so it can be edited before use
      reuseIfExists: false, // Always create new instance from wizard
    });

    setSelectedFunnelId(targetFunnelId || null);
  };

  // Handle new funnel creation
  const handleCreateFunnel = () => {
    if (!newFunnelName.trim()) {
      toast({
        title: t('common:error'),
        description: t('plugins.wizard.toast.noFunnelName'),
        variant: 'destructive',
      });
      return;
    }

    if (!selectedStepType) {
      toast({
        title: t('common:error'),
        description: t('plugins.wizard.toast.noStepType'),
        variant: 'destructive',
      });
      return;
    }

    // Create a simple funnel with one step
    // IMPORTANT: Step config does NOT include instanceId - no automatic mounting
    // User will manually mount the plugin after configuring it
    const stepId = `step_${Date.now()}`;

    const step = {
      id: stepId,
      name: t('plugins.wizard.newFunnel.firstStepName'),
      type: selectedStepType as any,
      index: 1,
      row: 0,
      isDefault: true,
      flowPosition: { x: 0, y: 0 },
      config: {
        path: `/${selectedStepType}`,
        metadata: {},
        // NO instanceId - user must publish and mount manually
      },
      conditions: {
        entry: true,
      },
    };

    const node = {
      id: step.id,
      name: step.name,
      type: step.type,
      isDefault: step.isDefault,
      config: step.config,
      position: step.flowPosition,
      index: step.index,
      row: step.row,
    };

    createFunnelMutation.mutate({
      storeId,
      config: {
        id: newFunnelName.toLowerCase().replace(/\s+/g, '-'),
        name: newFunnelName,
        description: t('plugins.wizard.newFunnel.defaultDescription', { name: plugin.name }),
        version: '1.0.0',
        steps: [step],
        nodes: [node],
        edges: [],
      } as any,
      isDefault: (funnels || []).length === 0, // Make default if first funnel
    });
  };

  // Reset wizard when closed
  React.useEffect(() => {
    if (!open) {
      setTimeout(() => {
        setCurrentStep('choose-context');
        setInstallContext(null);
        setSelectedFunnelId(null);
        setSelectedStepType(null);
        setNewFunnelName('');
      }, 300);
    }
  }, [open]);

  // If direct install or context install, show loading
  if (directInstallContext || (hasContext && instantiateMutation.isPending)) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-md">
          <div className="flex flex-col items-center justify-center gap-4 py-8">
            <Loader2 className="text-primary h-12 w-12 animate-spin" />
            <div className="text-center">
              <h3 className="font-semibold">{t('plugins.wizard.installing', { name: plugin.name })}</h3>
              <p className="text-muted-foreground text-sm">
                {hasContext
                  ? t('plugins.wizard.installingContextDesc')
                  : t('plugins.wizard.installingNewDesc')}
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShoppingBag className="h-5 w-5" />
            {t('plugins.wizard.dialogTitle', { name: plugin.name })}
          </DialogTitle>
          <DialogDescription>
            {deployment && (
              <Badge variant="outline" className="mt-2">
                v{deployment.version}
              </Badge>
            )}
          </DialogDescription>
        </DialogHeader>

        {/* Step 1: Choose Context */}
        {currentStep === 'choose-context' && (
          <div className="space-y-6">
            <div>
              <h3 className="mb-4 font-semibold">{t('plugins.wizard.chooseContext.question')}</h3>
              <div className="grid gap-3 sm:grid-cols-2">
                <button
                  onClick={() => {
                    setInstallContext('existing');
                    setCurrentStep('choose-funnel');
                  }}
                  className="border-border bg-card hover:border-primary hover:bg-accent group flex flex-col items-start gap-3 rounded-lg border-2 p-4 text-left transition-all"
                >
                  <div className="flex items-center gap-3">
                    <div className="bg-primary/10 text-primary flex h-10 w-10 items-center justify-center rounded-lg">
                      <FileText className="h-5 w-5" />
                    </div>
                    <h4 className="font-semibold">{t('plugins.wizard.chooseContext.existingTitle')}</h4>
                  </div>
                  <p className="text-muted-foreground text-sm">
                    {t('plugins.wizard.chooseContext.existingDesc')}
                  </p>
                </button>

                <button
                  onClick={() => {
                    setInstallContext('new');
                    setCurrentStep('choose-step-type');
                  }}
                  className="border-border bg-card hover:border-primary hover:bg-accent group flex flex-col items-start gap-3 rounded-lg border-2 p-4 text-left transition-all"
                >
                  <div className="flex items-center gap-3">
                    <div className="bg-primary/10 text-primary flex h-10 w-10 items-center justify-center rounded-lg">
                      <Plus className="h-5 w-5" />
                    </div>
                    <h4 className="font-semibold">{t('plugins.wizard.chooseContext.newTitle')}</h4>
                  </div>
                  <p className="text-muted-foreground text-sm">
                    {t('plugins.wizard.chooseContext.newDesc')}
                  </p>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Step 2a: Choose Existing Funnel */}
        {currentStep === 'choose-funnel' && (
          <div className="space-y-6">
            <div>
              <h3 className="mb-4 font-semibold">{t('plugins.wizard.chooseFunnel.title')}</h3>
              <Select value={selectedFunnelId || ''} onValueChange={setSelectedFunnelId}>
                <SelectTrigger>
                  <SelectValue placeholder={t('plugins.wizard.chooseFunnel.placeholder')} />
                </SelectTrigger>
                <SelectContent>
                  {isLoadingFunnels ? (
                    <div className="flex items-center justify-center p-4">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span className="text-muted-foreground ml-2 text-sm">{t('plugins.wizard.chooseFunnel.loading')}</span>
                    </div>
                  ) : (funnels || []).length === 0 ? (
                    <div className="text-muted-foreground p-4 text-center text-sm">
                      {t('plugins.wizard.chooseFunnel.empty')}
                    </div>
                  ) : (
                    (funnels || []).map((funnel) => (
                      <SelectItem key={funnel.id} value={funnel.id}>
                        <div className="flex items-center gap-2">
                          <span>{funnel.name}</span>
                          {funnel.isDefault && (
                            <Badge variant="secondary" className="text-[10px]">
                              {t('plugins.wizard.chooseFunnel.defaultBadge')}
                            </Badge>
                          )}
                        </div>
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>

            <div>
              <h3 className="mb-4 font-semibold">{t('plugins.wizard.choosePageType.title')}</h3>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                {AVAILABLE_STEP_TYPES.map((type) => {
                  const Icon = STEP_ICONS[type];
                  const isSelected = selectedStepType === type;
                  const isCompatible = compatibleStepTypes.has(type);
                  return (
                    <button
                      key={type}
                      onClick={() => isCompatible && setSelectedStepType(type)}
                      disabled={!isCompatible}
                      className={`relative flex flex-col items-center gap-2 rounded-lg border-2 p-3 text-center transition-all ${
                        !isCompatible
                          ? 'border-border bg-muted/30 cursor-not-allowed opacity-50'
                          : isSelected
                            ? 'border-primary bg-primary/5'
                            : 'border-border hover:border-primary/50 hover:bg-accent'
                      }`}
                      title={
                        !isCompatible
                          ? t('plugins.wizard.choosePageType.incompatibleTooltip', {
                              name: plugin.name,
                              type: STEP_TYPE_LABELS[type],
                            })
                          : ''
                      }
                    >
                      {isSelected && isCompatible && (
                        <Check className="text-primary absolute right-2 top-2 h-4 w-4" />
                      )}
                      <Icon
                        className={`h-6 w-6 ${!isCompatible ? 'text-muted-foreground/50' : isSelected ? 'text-primary' : 'text-muted-foreground'}`}
                      />
                      <span
                        className={`text-sm font-medium ${!isCompatible ? 'text-muted-foreground/70' : ''}`}
                      >
                        {STEP_TYPE_LABELS[type]}
                      </span>
                    </button>
                  );
                })}
              </div>
              {compatibleStepTypes.size === 1 && compatibleStepTypes.has('custom') ? (
                <div className="mt-4 rounded-lg border border-blue-200 bg-blue-50 p-4">
                  <p className="text-sm text-blue-900">
                    <strong>{t('plugins.wizard.choosePageType.customOnlyTitle')}</strong>{' '}
                    {t('plugins.wizard.choosePageType.customOnlyBody')}
                  </p>
                </div>
              ) : compatibleStepTypes.size < AVAILABLE_STEP_TYPES.length ? (
                <div className="mt-4 rounded-lg border border-blue-200 bg-blue-50 p-4">
                  <p className="text-sm text-blue-900">
                    <strong>{t('plugins.wizard.choosePageType.someUnavailableTitle')}</strong>{' '}
                    {t('plugins.wizard.choosePageType.someUnavailableBody', {
                      name: plugin.name,
                      types: Array.from(compatibleStepTypes)
                        .map((typeKey) => STEP_TYPE_LABELS[typeKey])
                        .join(', '),
                    })}
                  </p>
                </div>
              ) : null}
            </div>

            <div className="flex justify-between gap-3 pt-4">
              <Button variant="outline" onClick={() => setCurrentStep('choose-context')}>
                {t('common:back')}
              </Button>
              <Button
                onClick={() => handleInstantiate(selectedFunnelId || undefined)}
                disabled={!selectedFunnelId || !selectedStepType || instantiateMutation.isPending}
              >
                {instantiateMutation.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('plugins.wizard.actions.installing')}
                  </>
                ) : (
                  <>
                    {t('plugins.wizard.actions.continueToConfig')}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </>
                )}
              </Button>
            </div>
          </div>
        )}

        {/* Step 2b: Choose Step Type for New Funnel */}
        {currentStep === 'choose-step-type' && (
          <div className="space-y-6">
            <div>
              <h3 className="mb-4 font-semibold">{t('plugins.wizard.choosePageType.titleForNew')}</h3>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                {AVAILABLE_STEP_TYPES.map((type) => {
                  const Icon = STEP_ICONS[type];
                  const isSelected = selectedStepType === type;
                  const isCompatible = compatibleStepTypes.has(type);
                  return (
                    <button
                      key={type}
                      onClick={() => isCompatible && setSelectedStepType(type)}
                      disabled={!isCompatible}
                      className={`relative flex flex-col items-center gap-2 rounded-lg border-2 p-3 text-center transition-all ${
                        !isCompatible
                          ? 'border-border bg-muted/30 cursor-not-allowed opacity-50'
                          : isSelected
                            ? 'border-primary bg-primary/5'
                            : 'border-border hover:border-primary/50 hover:bg-accent'
                      }`}
                      title={
                        !isCompatible
                          ? t('plugins.wizard.choosePageType.incompatibleTooltip', {
                              name: plugin.name,
                              type: STEP_TYPE_LABELS[type],
                            })
                          : ''
                      }
                    >
                      {isSelected && isCompatible && (
                        <Check className="text-primary absolute right-2 top-2 h-4 w-4" />
                      )}
                      <Icon
                        className={`h-6 w-6 ${!isCompatible ? 'text-muted-foreground/50' : isSelected ? 'text-primary' : 'text-muted-foreground'}`}
                      />
                      <span
                        className={`text-sm font-medium ${!isCompatible ? 'text-muted-foreground/70' : ''}`}
                      >
                        {STEP_TYPE_LABELS[type]}
                      </span>
                    </button>
                  );
                })}
              </div>
              {compatibleStepTypes.size === 1 && compatibleStepTypes.has('custom') ? (
                <div className="mt-4 rounded-lg border border-blue-200 bg-blue-50 p-4">
                  <p className="text-sm text-blue-900">
                    <strong>{t('plugins.wizard.choosePageType.customOnlyTitle')}</strong>{' '}
                    {t('plugins.wizard.choosePageType.customOnlyBody')}
                  </p>
                </div>
              ) : compatibleStepTypes.size < AVAILABLE_STEP_TYPES.length ? (
                <div className="mt-4 rounded-lg border border-blue-200 bg-blue-50 p-4">
                  <p className="text-sm text-blue-900">
                    <strong>{t('plugins.wizard.choosePageType.someUnavailableTitle')}</strong>{' '}
                    {t('plugins.wizard.choosePageType.someUnavailableBody', {
                      name: plugin.name,
                      types: Array.from(compatibleStepTypes)
                        .map((typeKey) => STEP_TYPE_LABELS[typeKey])
                        .join(', '),
                    })}
                  </p>
                </div>
              ) : null}
            </div>

            <div className="flex justify-between gap-3 pt-4">
              <Button variant="outline" onClick={() => setCurrentStep('choose-context')}>
                {t('common:back')}
              </Button>
              <Button onClick={() => setCurrentStep('create-funnel')} disabled={!selectedStepType}>
                {t('common:next')}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Create New Funnel */}
        {currentStep === 'create-funnel' && (
          <div className="space-y-6">
            <div className="space-y-4">
              <div>
                <Label htmlFor="funnel-name">{t('plugins.wizard.newFunnel.nameLabel')}</Label>
                <Input
                  id="funnel-name"
                  placeholder={t('plugins.wizard.newFunnel.namePlaceholder')}
                  value={newFunnelName}
                  onChange={(e) => setNewFunnelName(e.target.value)}
                  autoFocus
                />
              </div>

              {selectedStepType &&
                (() => {
                  const Icon = STEP_ICONS[selectedStepType];
                  return (
                    <div className="bg-muted rounded-lg p-4">
                      <h4 className="mb-2 text-sm font-medium">{t('plugins.wizard.newFunnel.summaryTitle')}</h4>
                      <div className="text-muted-foreground flex items-center gap-2 text-sm">
                        {Icon && <Icon className="h-4 w-4" />}
                        <span>{t('plugins.wizard.newFunnel.summaryLine', { type: STEP_TYPE_LABELS[selectedStepType] })}</span>
                      </div>
                      <p className="text-muted-foreground mt-2 text-xs">
                        {t('plugins.wizard.newFunnel.summaryHint')}
                      </p>
                    </div>
                  );
                })()}
            </div>

            <div className="flex justify-between gap-3 pt-4">
              <Button variant="outline" onClick={() => setCurrentStep('choose-step-type')}>
                {t('common:back')}
              </Button>
              <Button
                onClick={handleCreateFunnel}
                disabled={
                  !newFunnelName.trim() || createFunnelMutation.isPending || instantiateMutation.isPending
                }
              >
                {createFunnelMutation.isPending || instantiateMutation.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('common:creating')}
                  </>
                ) : (
                  <>
                    {t('plugins.wizard.actions.createConfigure')}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
